/* eslint-disable no-magic-numbers */
import React, { memo, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import usePrefix from 'utils/usePrefix';
import { useApi, getApiUrl } from 'utils/api/useApi';
import { PaginatedList } from 'utils/api';
import { SchoolResponse } from 'utils/api/schools';
import { LOAD_ALL_PAGE_SIZE, MIN_SEARCH_QUERY_LENGTH } from 'utils/constants';

import NoResults from 'components/NoResults';
import EmptyList from 'components/EmptyList';
import Row from 'components/Row';

import { FormValues } from '..';
import List from './List';
import { ListWrapper, SearchList, SelectedList, ListTitle } from './styles';
import ToggleIcon from './ToggleIcon';

const url = getApiUrl('/schools');

interface Props {
  noticeDetails: FormValues;
  values: FormValues;
  setFieldValue: (field: string, value: unknown) => void;
}

const Target: React.FC<Props> = ({ noticeDetails, values, setFieldValue }) => {
  const t = usePrefix('Notices');
  const [nameFilter, setNameFilter] = useState({ school: '', city: '' });

  const initialSentTo = useMemo(
    () => new Set(noticeDetails.sent_to_schools ?? []),
    [noticeDetails.sent_to_schools],
  );
  const currentSentTo = useMemo(
    () => new Set(values.sent_to_schools ?? []),
    [values.sent_to_schools],
  );

  const { data: dataSchools } = useApi<PaginatedList<SchoolResponse[]>>(
    nameFilter.school.length < MIN_SEARCH_QUERY_LENGTH
      ? `${url}?page=0&size=${LOAD_ALL_PAGE_SIZE}`
      : `${url}?name=${nameFilter.school}&page=0&size=${LOAD_ALL_PAGE_SIZE}`,
    {
      method: 'GET',
    },
  );

  const [stillSent, pendingAdditions, pendingRemovals, availableSchools] = useMemo(() => {
    if (!Array.isArray(dataSchools?.content)) return [[], [], [], []] as SchoolResponse[][];

    const stillSent: SchoolResponse[] = [];
    const pendingAdditions: SchoolResponse[] = [];
    const pendingRemovals: SchoolResponse[] = [];
    const availableSchools: SchoolResponse[] = [];

    dataSchools!.content.forEach((school) => {
      const wasSent = initialSentTo.has(school.id);
      const isSent = currentSentTo.has(school.id);

      if (wasSent && isSent) stillSent.push(school);
      else if (!wasSent && isSent) pendingAdditions.push(school);
      else if (wasSent && !isSent) pendingRemovals.push(school);
      else if (school.valid_licence === true) availableSchools.push(school);
    });

    return [stillSent, pendingAdditions, pendingRemovals, availableSchools];
  }, [dataSchools, initialSentTo, currentSentTo]);

  const toggleSchool = (school: SchoolResponse) => {
    const next = new Set(currentSentTo);
    if (next.has(school.id)) next.delete(school.id);
    else next.add(school.id);
    setFieldValue('sent_to_schools', Array.from(next));
  };

  const onChangeFilter = (filter: object) => {
    setNameFilter((prevState) => ({ ...prevState, ...filter }));
  };

  const LIST_ITEM_HEIGHT = 48;

  return (
    <List
      title={t('schools')}
      name="schools"
      selected={pendingAdditions.length}
      placeholder={t('search')}
      onChange={(e) => onChangeFilter({ school: e.target.value })}
      resetFilter={() => onChangeFilter({ school: '' })}
      value={nameFilter.school}
      isSearchClearDisabled={nameFilter.school.length < 1}
    >
      <ListWrapper>
        {pendingRemovals.length > 0 && (
          <>
            <ListTitle>{t('removed')}</ListTitle>
            <SelectedList>
              {pendingRemovals.map((school) => (
                <Row
                  key={school.id}
                  title={school.name}
                  labels={[
                    school.name,
                    school.voivodeship,
                    school.locality,
                    school.street,
                    school.number,
                  ]}
                  onClick={() => toggleSchool(school)}
                  cellChildren={<ToggleIcon image={'plus'} />}
                  selected
                />
              ))}
            </SelectedList>
          </>
        )}
        {stillSent.length > 0 && (
          <>
            <ListTitle>{t('sent_to')}</ListTitle>
            <SelectedList>
              {stillSent.map((school) => (
                <Row
                  key={school.id}
                  title={school.name}
                  labels={[
                    school.name,
                    school.voivodeship,
                    school.locality,
                    school.street,
                    school.number,
                  ]}
                  disabled={school.valid_licence !== true}
                  onClick={() => toggleSchool(school)}
                  cellChildren={<ToggleIcon image={'minus'} />}
                  clickable={true}
                />
              ))}
            </SelectedList>
          </>
        )}
      </ListWrapper>

      {availableSchools.length || pendingAdditions.length ? (
        <ListWrapper>
          {pendingAdditions.length > 0 && (
            <>
              <ListTitle>{t('selected')}</ListTitle>
              <SelectedList>
                {pendingAdditions.map((school) => (
                  <Row
                    key={school.id}
                    title={school.name}
                    labels={[
                      school.name,
                      school.voivodeship,
                      school.locality,
                      school.street,
                      school.number,
                    ]}
                    onClick={() => toggleSchool(school)}
                    cellChildren={<ToggleIcon image={'minus'} />}
                    selected
                  />
                ))}
              </SelectedList>
            </>
          )}
          {availableSchools.length > 0 && (
            <>
              <ListTitle>{t('optional')}</ListTitle>
              <SearchList height={`${availableSchools.length * LIST_ITEM_HEIGHT}px`}>
                <AutoSizer disableWidth>
                  {() => (
                    <FixedSizeList
                      height={availableSchools.length * LIST_ITEM_HEIGHT}
                      width="100%"
                      itemCount={availableSchools.length}
                      itemSize={LIST_ITEM_HEIGHT}
                      innerElementType="ul"
                    >
                      {({ index, ...props }) => (
                        <Row
                          title={availableSchools[index].name}
                          labels={[
                            availableSchools[index].name,
                            availableSchools[index].voivodeship,
                            availableSchools[index].locality,
                            availableSchools[index].street,
                            availableSchools[index].number,
                          ]}
                          onClick={() => toggleSchool(availableSchools[index])}
                          cellChildren={<ToggleIcon image={'plus'} />}
                          {...props}
                        />
                      )}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </SearchList>
            </>
          )}
        </ListWrapper>
      ) : (
        <EmptyList>
          <NoResults margin="0" title={t('no_schools')} />
        </EmptyList>
      )}
    </List>
  );
};

export default memo(Target);
